/**
 * Pure budget math. Implements docs/development/domain-model.md §4. All amounts are base-currency minor units.
 * The server uses computeBudget() for the month view; the client reuses the target helpers for live updates.
 */
import {
  addMonths,
  countWeekdays,
  daysBetween,
  monthOf,
  monthsBetween,
  type IsoDate,
  type Month,
} from './dates';
import { MINOR } from './money';

export const TARGET_TYPES = ['spending', 'savings_balance', 'debt'] as const;
export type TargetType = (typeof TARGET_TYPES)[number];
export const TARGET_CADENCES = ['weekly', 'monthly', 'yearly', 'custom'] as const;
export type TargetCadence = (typeof TARGET_CADENCES)[number];
export type TargetMode = 'set_aside' | 'refill';
export type MoneyState = 'pos' | 'warn' | 'neg' | 'zero';

export interface Target {
  type: TargetType;
  amount: number;
  cadence: TargetCadence;
  dueDate: IsoDate | null;
  mode: TargetMode;
  snoozedMonth: Month | null;
}

export const UNCATEGORIZED = '__uncategorized';

/** Round a positive amount up to whole major units, so needs read "3.000", not "2.999,67". */
const ceilMajor = (minor: number) => Math.ceil(minor / MINOR) * MINOR;

export const isSnoozed = (t: Target | null | undefined, month: Month) => !!t && t.snoozedMonth === month;

/** What the target asks for in this month, before subtracting what is already funded. */
export function monthlyNeed(
  t: Target,
  month: Month,
  carryIn: number,
  weekStart: 'mon' | 'sun' = 'mon',
): number {
  switch (t.type) {
    case 'spending': {
      if (t.mode === 'refill') return t.amount;
      if (t.cadence === 'weekly') {
        const wd = t.dueDate ? new Date(t.dueDate + 'T00:00:00Z').getUTCDay() : weekStart === 'mon' ? 1 : 0;
        return t.amount * countWeekdays(month, wd);
      }
      if (t.cadence === 'yearly') {
        if (!t.dueDate) return ceilMajor(t.amount / 12);
        return spreadUntil(t.amount, carryIn, month, nextYearlyDue(t.dueDate, month));
      }
      return t.amount;
    }
    case 'savings_balance':
      if (!t.dueDate) return 0;
      return spreadUntil(t.amount, carryIn, month, monthOf(t.dueDate));
    case 'debt':
      return t.amount;
  }
}

/** Remaining amount spread evenly over the months from `month` up to and including `dueMonth`. */
function spreadUntil(total: number, balance: number, month: Month, dueMonth: Month): number {
  const remaining = total - balance;
  if (remaining <= 0) return 0;
  const months = monthsBetween(month, dueMonth) + 1;
  if (months <= 1) return remaining;
  return ceilMajor(remaining / months);
}

function nextYearlyDue(dueDate: IsoDate, month: Month): Month {
  const mm = dueDate.slice(5, 7);
  const year = Number(month.slice(0, 4));
  const thisYear = `${year}-${mm}`;
  return thisYear >= month ? thisYear : `${year + 1}-${mm}`;
}

/**
 * How much of this month's need is already covered. Refill targets count the carried-in balance; date-based
 * targets already subtracted the balance at month start in monthlyNeed(), so only this month's assignment counts.
 */
export function funded(t: Target, carryIn: number, assigned: number): number {
  return t.type === 'spending' && t.mode === 'refill' ? carryIn + assigned : assigned;
}

export function need(
  t: Target | null | undefined,
  month: Month,
  carryIn: number,
  assigned: number,
  weekStart: 'mon' | 'sun' = 'mon',
): number {
  if (!t || isSnoozed(t, month)) return 0;
  return Math.max(0, monthlyNeed(t, month, carryIn, weekStart) - funded(t, carryIn, assigned));
}

export function moneyState(available: number, needAmount: number): MoneyState {
  if (available < 0) return 'neg';
  if (needAmount > 0) return 'warn';
  return available > 0 ? 'pos' : 'zero';
}

export const rtaState = (rta: number): MoneyState => (rta < 0 ? 'neg' : rta === 0 ? 'zero' : 'pos');

/** Assigned value that exactly meets the target this month ("Fund target" quick chip). */
export function fundTargetValue(
  t: Target,
  month: Month,
  carryIn: number,
  assigned: number,
  weekStart: 'mon' | 'sun' = 'mon',
) {
  return assigned + need(t, month, carryIn, assigned, weekStart);
}

export interface CategoryMonth {
  carryIn: number;
  assigned: number;
  activity: number;
  available: number;
  monthlyNeed: number;
  funded: number;
  need: number;
  state: MoneyState;
}

export interface BudgetData {
  categories: { id: string; target: Target | null }[];
  assignments: { categoryId: string; month: Month; amount: number }[];
  /** Base-currency activity per category and month. categoryId null = uncategorized. */
  activity: { categoryId: string | null; month: Month; amount: number }[];
  /** Inflows categorised to Ready to Assign, per month. */
  income: { month: Month; amount: number }[];
  /** FX revaluation + realised FX per month (§4.5). */
  fx: { month: Month; amount: number }[];
  weekStart?: 'mon' | 'sun';
}

export interface BudgetMonth {
  month: Month;
  categories: Record<string, CategoryMonth>;
  uncategorized: { carryIn: number; activity: number; available: number };
  rta: number;
  incomeThisMonth: number;
  fxThisMonth: number;
  /** Cash overspending from the previous month, taken from this month's RTA. */
  overspentLastMonth: number;
  assignedInFuture: number;
}

const key = (id: string, m: Month) => `${id}|${m}`;

function sumBy<T>(rows: T[], k: (r: T) => string, v: (r: T) => number) {
  const map = new Map<string, number>();
  for (const r of rows) map.set(k(r), (map.get(k(r)) ?? 0) + v(r));
  return map;
}

/** Compute the full budget state for `month`, rolling forward from the earliest month with data. */
export function computeBudget(data: BudgetData, month: Month): BudgetMonth {
  const weekStart = data.weekStart ?? 'mon';
  const assigned = sumBy(
    data.assignments,
    (a) => key(a.categoryId, a.month),
    (a) => a.amount,
  );
  const activity = sumBy(
    data.activity,
    (a) => key(a.categoryId ?? UNCATEGORIZED, a.month),
    (a) => a.amount,
  );

  const months = [
    ...data.assignments.map((a) => a.month),
    ...data.activity.map((a) => a.month),
    ...data.income.map((a) => a.month),
    ...data.fx.map((a) => a.month),
  ];
  const start = months.reduce((min, m) => (m < min ? m : min), month);

  const ids = [...data.categories.map((c) => c.id), UNCATEGORIZED];
  const carry = new Map<string, number>(ids.map((id) => [id, 0]));
  let overspentBefore = 0;
  let overspentLast = 0;
  let current: Record<string, CategoryMonth> = {};
  let uncat = { carryIn: 0, activity: 0, available: 0 };

  for (let m = start; m <= month; m = addMonths(m, 1)) {
    let overspent = 0;
    const rows: Record<string, CategoryMonth> = {};
    for (const c of data.categories) {
      const carryIn = carry.get(c.id) ?? 0;
      const a = assigned.get(key(c.id, m)) ?? 0;
      const act = activity.get(key(c.id, m)) ?? 0;
      const available = carryIn + a + act;
      const mn = c.target && !isSnoozed(c.target, m) ? monthlyNeed(c.target, m, carryIn, weekStart) : 0;
      const f = c.target ? funded(c.target, carryIn, a) : a;
      const n = need(c.target, m, carryIn, a, weekStart);
      rows[c.id] = {
        carryIn,
        assigned: a,
        activity: act,
        available,
        monthlyNeed: mn,
        funded: f,
        need: n,
        state: moneyState(available, n),
      };
      carry.set(c.id, Math.max(available, 0));
      if (available < 0) overspent += -available;
    }
    const uCarry = carry.get(UNCATEGORIZED) ?? 0;
    const uAct = activity.get(key(UNCATEGORIZED, m)) ?? 0;
    uncat = { carryIn: uCarry, activity: uAct, available: uCarry + uAct };
    carry.set(UNCATEGORIZED, Math.max(uncat.available, 0));
    if (uncat.available < 0) overspent += -uncat.available;

    if (m === month) {
      current = rows;
    } else {
      overspentBefore += overspent;
      overspentLast = m === addMonths(month, -1) ? overspent : 0;
    }
  }

  const incomeTo = data.income.filter((i) => i.month <= month).reduce((s, i) => s + i.amount, 0);
  const fxTo = data.fx.filter((i) => i.month <= month).reduce((s, i) => s + i.amount, 0);
  const assignedAll = data.assignments.reduce((s, a) => s + a.amount, 0);
  const assignedInFuture = data.assignments.filter((a) => a.month > month).reduce((s, a) => s + a.amount, 0);

  return {
    month,
    categories: current,
    uncategorized: uncat,
    rta: incomeTo + fxTo - assignedAll - overspentBefore,
    incomeThisMonth: data.income.filter((i) => i.month === month).reduce((s, i) => s + i.amount, 0),
    fxThisMonth: data.fx.filter((i) => i.month === month).reduce((s, i) => s + i.amount, 0),
    overspentLastMonth: overspentLast,
    assignedInFuture,
  };
}

/** Recompute one category after its Assigned value changed (client-side live typing). */
export function withAssigned(
  row: CategoryMonth,
  target: Target | null,
  month: Month,
  newAssigned: number,
  weekStart: 'mon' | 'sun' = 'mon',
): CategoryMonth {
  const available = row.carryIn + newAssigned + row.activity;
  const n = need(target, month, row.carryIn, newAssigned, weekStart);
  return {
    ...row,
    assigned: newAssigned,
    available,
    funded: target ? funded(target, row.carryIn, newAssigned) : newAssigned,
    need: n,
    state: moneyState(available, n),
  };
}

/**
 * Age of Money (§4.8): FIFO — each outflow consumes the oldest unspent inflow. Returns the average age in days
 * of the money spent by the last `window` outflows up to `asOf`, or null when there are no outflows yet.
 */
export function ageOfMoney(
  flows: { date: IsoDate; amount: number }[],
  asOf?: IsoDate,
  window = 10,
): number | null {
  const sorted = flows
    .filter((f) => f.amount !== 0 && (!asOf || f.date <= asOf))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : b.amount - a.amount));
  const buckets: { date: IsoDate; left: number }[] = [];
  let head = 0;
  const ages: number[] = [];
  for (const f of sorted) {
    if (f.amount > 0) {
      buckets.push({ date: f.date, left: f.amount });
      continue;
    }
    let need = -f.amount;
    let weighted = 0;
    let covered = 0;
    while (need > 0 && head < buckets.length) {
      const b = buckets[head]!;
      const take = Math.min(b.left, need);
      weighted += take * daysBetween(b.date, f.date);
      covered += take;
      b.left -= take;
      need -= take;
      if (b.left === 0) head++;
    }
    // Money spent beyond what came in has age 0 (it was "borrowed" from the future).
    ages.push(covered + need > 0 ? weighted / (covered + need) : 0);
  }
  if (!ages.length) return null;
  const last = ages.slice(-window);
  return Math.round(last.reduce((s, a) => s + a, 0) / last.length);
}
